/**
 * Two-step Classify modal for the Work Log Review page — task 159d.
 *
 * Replaces the disabled Classify buttons left by 159c. Drives the 159b
 * `POST /api/v1/orgs/:orgId/work-log-review/classify` endpoint.
 *
 * Step 1 — pick destination Client → Project (searchable, recently-used
 *   floats to top, "Internal / Not Set" is selectable but greyed because
 *   fallback events already land there per locked decision #8).
 *
 * Step 2 — "Make this permanent?" — a pre-filled rule the user can accept,
 *   edit, or skip (locked decisions #9, #10, #11). Conflict warning lands
 *   inline after the response (locked decision #12). Priority-bump warning
 *   fires client-side as the user types.
 *
 * Apply calls 159b once and surfaces the response. The endpoint applies the
 * manual override + (optionally) creates the rule in one transaction; if
 * rule creation fails, neither writes (verified server-side, see 159b
 * integration tests).
 *
 * The page (WorkLogReviewPage) owns the modal-open state, the scope, and
 * the success-banner state. This component owns the step machine and the
 * form.
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Button } from '@/components/primitives/Button';
import { InfoBanner } from '@/components/primitives/InfoBanner';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { ApiError } from '@/lib/api';
import { useClients } from '@/hooks/useClients';
import { useProjects } from '@/hooks/useProjects';
import { useRules } from '@/hooks/useRules';
import {
  useClassifyWorkLog,
  type ClassifyRule,
  type ClassifyScope,
  type ClassifyScopeFilter,
  type ClassifyWorkLogBody,
  type ClassifyWorkLogResult,
} from '@/hooks/useClassifyWorkLog';
import type {
  GroupByDimension,
  WorkLogReviewEventRow,
  WorkLogReviewGroupRow,
} from '@/types/workLogReview';
import type { Client, Project } from '@/types/entities';
import {
  deriveRuleSuggestion,
  type DerivedRule,
  type RuleField,
  type RuleSuggestion,
} from './deriveRuleSuggestion';
import styles from './ClassifyModal.module.css';

// ── Public types ────────────────────────────────────────────────────────────

export type ClassifyTarget =
  | {
      kind: 'group';
      row: WorkLogReviewGroupRow;
      filter: ClassifyScopeFilter;
      eventsInScope: readonly WorkLogReviewEventRow[];
    }
  | { kind: 'events'; events: readonly WorkLogReviewEventRow[] };

export interface ClassifyModalProps {
  open: boolean;
  onClose: () => void;
  target: ClassifyTarget | null;
  groupBy: readonly GroupByDimension[];
  onSuccess: (
    result: ClassifyWorkLogResult,
    context: { project: ProjectOption; ruleSubmitted: boolean },
  ) => void;
}

// ── Local types ─────────────────────────────────────────────────────────────

interface ProjectOption {
  id: string;
  name: string;
  clientId: string | null;
  clientName: string;
  isInternalFallback: boolean;
}

const RECENT_PROJECTS_KEY = 'kanjoy:wlr.recentProjects';
const RECENT_LIMIT = 5;
const FIELD_LABELS: Record<RuleField, string> = {
  app: 'App',
  title: 'Title',
  url: 'URL',
  site: 'Site',
  ip: 'IP',
  document: 'Document',
};

// ── Recent-projects localStorage helpers ────────────────────────────────────

function readRecentProjects(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_PROJECTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string').slice(0, RECENT_LIMIT);
  } catch {
    // localStorage may throw in privacy mode; degrade gracefully.
    return [];
  }
}

function writeRecentProject(projectId: string): void {
  try {
    const current = readRecentProjects();
    const next = [projectId, ...current.filter((id) => id !== projectId)].slice(0, RECENT_LIMIT);
    localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(next));
  } catch {
    // Ignore.
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function isInternalFallback(p: Project): boolean {
  // Mirror AddRulePanel.tsx's identification of the classifier's terminal
  // fallback (task 131 D6). Both `slug` and `clientSlug` are required —
  // `systemOwned` alone is not enough because Personal/Not Set is also
  // systemOwned but is rule-targetable.
  return p.systemOwned && p.slug === 'not-set' && p.clientSlug === 'internal';
}

function buildProjectOptions(
  clients: readonly Client[],
  projects: readonly Project[],
): ProjectOption[] {
  const clientById = new Map<string, Client>();
  for (const c of clients) clientById.set(c.id, c);

  return projects
    .filter((p) => !p.archivedAt)
    .map((p): ProjectOption => {
      const client = p.clientId ? clientById.get(p.clientId) : null;
      return {
        id: p.id,
        name: p.name,
        clientId: p.clientId,
        clientName: client?.name ?? 'Org-level',
        isInternalFallback: isInternalFallback(p),
      };
    });
}

function projectMatchesQuery(option: ProjectOption, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  return option.name.toLowerCase().includes(q) || option.clientName.toLowerCase().includes(q);
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

function buildRuleName(option: ProjectOption, derivedValue: string): string {
  const left = truncate(derivedValue, 30);
  const right = truncate(`${option.clientName} / ${option.name}`, 40);
  return truncate(`${left} → ${right}`, 80);
}

function errorMessageFor(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 422 && err.code === 'INVALID_REGEX') {
      return "That rule's value isn't a valid regex pattern.";
    }
    if (err.status === 409) {
      return 'A rule with that name already exists. Please pick a different name.';
    }
    if (err.status === 403 || err.status === 401) {
      return 'You don’t have permission to classify these events.';
    }
    if (err.status >= 500) {
      return 'Something went wrong on our side. Please try again in a moment.';
    }
  }
  return 'Could not classify these events. Please try again.';
}

// ── Component ───────────────────────────────────────────────────────────────

export function ClassifyModal({ open, onClose, target, groupBy, onSuccess }: ClassifyModalProps) {
  const subtitle =
    target?.kind === 'group'
      ? 'Classify this group and optionally promote the choice into a rule.'
      : target?.kind === 'events'
        ? `Classify ${target.events.length} selected event${target.events.length === 1 ? '' : 's'} and optionally promote the choice into a rule.`
        : '';

  return (
    <SlidePanel
      open={open && target !== null}
      onClose={onClose}
      title="Classify activity"
      subtitle={subtitle}
      width="md"
    >
      {target !== null && (
        <ClassifyModalBody
          target={target}
          groupBy={groupBy}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
    </SlidePanel>
  );
}

// ── Modal body — owns the step machine ──────────────────────────────────────

interface ClassifyModalBodyProps {
  target: ClassifyTarget;
  groupBy: readonly GroupByDimension[];
  onClose: () => void;
  onSuccess: (
    result: ClassifyWorkLogResult,
    context: { project: ProjectOption; ruleSubmitted: boolean },
  ) => void;
}

function ClassifyModalBody({ target, groupBy, onClose, onSuccess }: ClassifyModalBodyProps) {
  const { data: clients } = useClients();
  const { data: projects } = useProjects();
  const { data: rules } = useRules();
  const classify = useClassifyWorkLog();

  const projectOptions = useMemo(() => buildProjectOptions(clients, projects), [clients, projects]);

  const [step, setStep] = useState<1 | 2>(1);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [response, setResponse] = useState<ClassifyWorkLogResult | null>(null);

  const goNext = (option: ProjectOption) => {
    setSelectedProject(option);
    setStep(2);
    setServerError(null);
  };

  const goBack = () => {
    setStep(1);
    setServerError(null);
    setResponse(null);
  };

  const eventsForDerivation: readonly WorkLogReviewEventRow[] =
    target.kind === 'group' ? target.eventsInScope : target.events;

  const groupKey = target.kind === 'group' ? target.row.key : undefined;

  const suggestion = useMemo(
    () =>
      deriveRuleSuggestion({
        groupBy,
        groupKey,
        events: eventsForDerivation,
      }),
    [groupBy, groupKey, eventsForDerivation],
  );

  const handleApply = async (
    rule: ClassifyRule | undefined,
    project: ProjectOption,
  ): Promise<void> => {
    setServerError(null);
    const scope: ClassifyScope =
      target.kind === 'group'
        ? { kind: 'group', key: target.row.key, filter: target.filter }
        : { kind: 'events', eventIds: target.events.map((e) => e.id) };
    const body: ClassifyWorkLogBody = {
      scope,
      projectId: project.id,
      ...(rule ? { rule } : {}),
    };
    try {
      const result = await classify.mutateAsync(body);
      writeRecentProject(project.id);
      setResponse(result);
      const conflict = result.conflictReport?.eventsCaughtByExistingRule ?? 0;
      if (rule && conflict > 0) {
        // Stay on Step 2 so the user can read the conflict banner.
        return;
      }
      // Otherwise close immediately and let the page render the success.
      onSuccess(result, { project, ruleSubmitted: rule !== undefined });
      onClose();
    } catch (err) {
      setServerError(errorMessageFor(err));
    }
  };

  if (step === 1 || selectedProject === null) {
    return (
      <ClassifyStep1Picker projectOptions={projectOptions} onCancel={onClose} onNext={goNext} />
    );
  }
  return (
    <ClassifyStep2RuleForm
      project={selectedProject}
      suggestion={suggestion}
      eventsInScope={eventsForDerivation}
      rules={rules}
      response={response}
      submitting={classify.isPending}
      serverError={serverError}
      onBack={goBack}
      onCancel={onClose}
      onClose={() => {
        // "Done" pathway after a conflict response — emit success and close.
        if (response) {
          onSuccess(response, {
            project: selectedProject,
            ruleSubmitted: response.createdRule !== null,
          });
        }
        onClose();
      }}
      onApply={handleApply}
    />
  );
}

// ── Step 1 ──────────────────────────────────────────────────────────────────

interface Step1Props {
  projectOptions: readonly ProjectOption[];
  onCancel: () => void;
  onNext: (option: ProjectOption) => void;
}

function ClassifyStep1Picker({ projectOptions, onCancel, onNext }: Step1Props) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputId = useId();
  const listId = useId();
  const internalFallbackId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the search input when the picker mounts. Using a ref + useEffect
  // (rather than the `autoFocus` JSX attribute) keeps jsx-a11y/no-autofocus
  // happy while preserving the keyboard-only flow inside the SlidePanel.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const recentIds = useMemo(() => readRecentProjects(), []);

  const filtered = useMemo(
    () => projectOptions.filter((p) => projectMatchesQuery(p, query)),
    [projectOptions, query],
  );

  // Recently-used floats to top in registration order, then the rest in
  // their natural (alphabetical via the option list) order.
  const ordered = useMemo(() => {
    const recent: ProjectOption[] = [];
    const recentSet = new Set(recentIds);
    for (const id of recentIds) {
      const found = filtered.find((p) => p.id === id);
      if (found) recent.push(found);
    }
    const rest = filtered.filter((p) => !recentSet.has(p.id));
    return { recent, rest, all: [...recent, ...rest] };
  }, [filtered, recentIds]);

  // Keep the active row index in range as filtering changes. The list size
  // and `activeIdx` together drive a derived index — we read the clamped
  // value during render rather than calling setState in an effect (which
  // would trigger a cascading render).
  const safeActiveIdx = ordered.all.length === 0 ? 0 : Math.min(activeIdx, ordered.all.length - 1);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (ordered.all.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(Math.min(safeActiveIdx + 1, ordered.all.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(Math.max(safeActiveIdx - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = ordered.all[safeActiveIdx];
      if (picked) onNext(picked);
    }
  };

  return (
    <>
      <div className={styles.body}>
        <h3 className={styles.stepHeading}>Where should this go?</h3>

        <div className={styles.field}>
          <label htmlFor={inputId} className={styles.label}>
            Find a client or project
          </label>
          <input
            ref={inputRef}
            id={inputId}
            type="search"
            className={styles.search}
            placeholder="Search by client or project name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
          />
        </div>

        <ul
          id={listId}
          role="listbox"
          className={styles.optionList}
          data-testid="wlr-classify-projects"
        >
          {ordered.recent.length > 0 && (
            <>
              <li className={styles.optionGroupLabel} role="presentation">
                Recently used
              </li>
              {ordered.recent.map((opt, i) => (
                <ProjectOptionRow
                  key={opt.id}
                  option={opt}
                  active={i === safeActiveIdx}
                  onPick={() => onNext(opt)}
                  onHover={() => setActiveIdx(i)}
                  internalFallbackDescId={internalFallbackId}
                />
              ))}
              {ordered.rest.length > 0 && (
                <li className={styles.optionDivider} role="presentation" aria-hidden="true" />
              )}
            </>
          )}
          {ordered.rest.map((opt, i) => {
            const idx = ordered.recent.length + i;
            return (
              <ProjectOptionRow
                key={opt.id}
                option={opt}
                active={idx === safeActiveIdx}
                onPick={() => onNext(opt)}
                onHover={() => setActiveIdx(idx)}
                internalFallbackDescId={internalFallbackId}
              />
            );
          })}
          {ordered.all.length === 0 && (
            <li className={styles.emptyOption} role="presentation">
              No clients or projects match your search.
            </li>
          )}
        </ul>

        {/* Hidden description referenced by the Internal/Not Set option's
            aria-describedby. Keeps the in-row text short while still
            spelling out the no-op semantics for assistive tech. */}
        <span id={internalFallbackId} className={styles.srOnly}>
          Internal — Not Set is where unclassified activity already lives. Selecting it has no
          effect on these events.
        </span>
      </div>
      <div className={styles.footer}>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </>
  );
}

interface ProjectOptionRowProps {
  option: ProjectOption;
  active: boolean;
  onPick: () => void;
  onHover: () => void;
  internalFallbackDescId: string;
}

function ProjectOptionRow({
  option,
  active,
  onPick,
  onHover,
  internalFallbackDescId,
}: ProjectOptionRowProps) {
  const isFallback = option.isInternalFallback;
  return (
    <li
      role="option"
      aria-selected={active}
      aria-describedby={isFallback ? internalFallbackDescId : undefined}
      className={[
        styles.option,
        active ? styles.optionActive : '',
        isFallback ? styles.optionMuted : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onPick}
      onMouseEnter={onHover}
      data-testid={isFallback ? 'wlr-classify-internal-fallback' : 'wlr-classify-project'}
    >
      <span className={styles.optionPrimary}>{option.name}</span>
      <span className={styles.optionSecondary}>{option.clientName}</span>
      {isFallback && (
        <span className={styles.optionNote}>
          Already where unclassified activity lives — selecting it has no effect.
        </span>
      )}
    </li>
  );
}

// ── Step 2 ──────────────────────────────────────────────────────────────────

interface Step2Props {
  project: ProjectOption;
  suggestion: RuleSuggestion;
  eventsInScope: readonly WorkLogReviewEventRow[];
  rules: readonly { priority: number }[];
  response: ClassifyWorkLogResult | null;
  submitting: boolean;
  serverError: string | null;
  onBack: () => void;
  onCancel: () => void;
  onClose: () => void;
  onApply: (rule: ClassifyRule | undefined, project: ProjectOption) => Promise<void>;
}

function ClassifyStep2RuleForm({
  project,
  suggestion,
  eventsInScope,
  rules,
  response,
  submitting,
  serverError,
  onBack,
  onCancel,
  onClose,
  onApply,
}: Step2Props) {
  const initialDerived: DerivedRule | null =
    suggestion && 'field' in suggestion ? suggestion : null;
  const isFallbackAppMode = suggestion !== null && 'kind' in suggestion;
  // Once the user accepts the fallback-app suggestion, the derived rule
  // becomes an `app` rule on the suggested value. State holds the active
  // derived rule (post-fallback-acceptance if applicable).
  const [activeDerived, setActiveDerived] = useState<DerivedRule | null>(initialDerived);

  // Reset internal state when the suggestion identity changes (e.g. modal
  // re-opened against a different group). `useState`'s initial value runs
  // only on first render, so an effect is needed to track changes.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing props→state on identity change; useState initial value only fires once
    setActiveDerived(initialDerived);
    // initialDerived is structural; serialise to drive the deps array.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- structural compare via JSON.stringify is the intent
  }, [JSON.stringify(initialDerived)]);

  const maxPriority = useMemo(
    () => (rules.length === 0 ? 0 : Math.max(...rules.map((r) => r.priority))),
    [rules],
  );
  const defaultPriority = maxPriority + 1;

  const [createRule, setCreateRule] = useState(activeDerived !== null);
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [priority, setPriority] = useState<number>(defaultPriority);

  // Re-seed the form when the active derived rule changes (initial mount,
  // user accepts the fallback-app suggestion, or modal re-opens).
  useEffect(() => {
    if (activeDerived) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- re-seeding form state from derived props on identity change
      setCreateRule(true);
      setName(buildRuleName(project, activeDerived.value));
      setValue(activeDerived.value);
    } else {
      setCreateRule(false);
      setName('');
      setValue('');
    }
    setPriority(defaultPriority);
    // We deliberately re-seed when the derivation, project, or the existing
    // priority ceiling changes — a fresh modal session should always start
    // from defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional reseed on these inputs
  }, [activeDerived?.field, activeDerived?.value, project.id, defaultPriority]);

  const acceptFallbackApp = () => {
    if (suggestion && 'kind' in suggestion) {
      if (suggestion.suggestedApp.length === 0) return;
      setActiveDerived({ field: 'app', value: suggestion.suggestedApp });
    }
  };

  const conflict = response?.conflictReport ?? null;
  const conflictTriggered = conflict !== null && conflict.eventsCaughtByExistingRule > 0;

  const trimmedName = name.trim();
  const trimmedValue = value.trim();

  const formInvalid =
    createRule &&
    (trimmedName.length === 0 || trimmedValue.length === 0 || !Number.isInteger(priority));

  const priorityBumpCount = useMemo(() => {
    if (!createRule) return 0;
    return rules.filter((r) => r.priority >= priority).length;
  }, [createRule, rules, priority]);
  const showPriorityBump = createRule && priority < defaultPriority && priorityBumpCount > 0;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (formInvalid || submitting || conflictTriggered) return;

    let rule: ClassifyRule | undefined;
    if (createRule && activeDerived) {
      rule = {
        name: trimmedName,
        priority,
        condition: { type: 'pattern', field: activeDerived.field, value: trimmedValue },
      };
    }
    await onApply(rule, project);
  };

  return (
    <form onSubmit={onSubmit}>
      <div className={styles.body}>
        <h3 className={styles.stepHeading}>Make this permanent?</h3>
        <p className={styles.destinationLine}>
          Destination: <strong>{project.clientName}</strong> › <strong>{project.name}</strong>
          {project.isInternalFallback && (
            <span className={styles.subtleNote}>
              {' '}
              (these events already live here — applying has no effect)
            </span>
          )}
        </p>

        {/* No-rule branches: appFileName fallback or no common denominator. */}
        {suggestion === null && (
          <InfoBanner tone="info" title="No rule will be created">
            These events don’t share a field a rule can match on. The classification will apply to
            the events you’re reviewing, but no permanent rule will be created.
          </InfoBanner>
        )}
        {isFallbackAppMode && activeDerived === null && (
          <InfoBanner tone="info" title="No rule can match on file name">
            <p>Rules can match on app, title, URL, site, IP, or document — but not on file name.</p>
            {suggestion && 'kind' in suggestion && suggestion.suggestedApp.length > 0 ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={acceptFallbackApp}
                data-testid="wlr-classify-use-app"
              >
                Use App: {suggestion.suggestedApp}
              </Button>
            ) : (
              <p className={styles.subtleNote}>
                These events also don’t share a common app, so no rule can be suggested.
              </p>
            )}
          </InfoBanner>
        )}

        {conflictTriggered && (
          <InfoBanner tone="warning" title="A higher-priority rule already exists">
            {formatConflictMessage(conflict)}
          </InfoBanner>
        )}

        {serverError && <InfoBanner tone="danger">{serverError}</InfoBanner>}

        {activeDerived && !conflictTriggered && (
          <div className={styles.ruleForm}>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={createRule}
                onChange={(e) => setCreateRule(e.target.checked)}
                data-testid="wlr-classify-rule-toggle"
              />
              <span>Create a rule from this classification</span>
            </label>

            <fieldset
              className={styles.fieldset}
              disabled={!createRule}
              aria-disabled={!createRule}
            >
              <legend className={styles.srOnly}>Rule details</legend>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="wlr-classify-name">
                  Rule name
                </label>
                <input
                  id="wlr-classify-name"
                  type="text"
                  className={styles.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={200}
                  data-testid="wlr-classify-name"
                />
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <span className={styles.label}>When</span>
                  <span className={styles.readonlyField}>
                    {FIELD_LABELS[activeDerived.field]} contains
                  </span>
                </div>
                <div className={styles.field} style={{ flex: 1 }}>
                  <label className={styles.label} htmlFor="wlr-classify-value">
                    Value
                  </label>
                  <input
                    id="wlr-classify-value"
                    type="text"
                    className={styles.input}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    data-testid="wlr-classify-value"
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="wlr-classify-priority">
                  Priority
                </label>
                <input
                  id="wlr-classify-priority"
                  type="number"
                  step={1}
                  className={styles.numberInput}
                  value={priority}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n) && Number.isInteger(n)) setPriority(n);
                  }}
                  data-testid="wlr-classify-priority"
                />
                <span className={styles.helpText}>
                  Lower numbers run first. New rules default to the lowest priority so they don’t
                  preempt existing ones.
                </span>
              </div>

              {showPriorityBump && (
                <InfoBanner tone="warning">
                  Higher priority will preempt {priorityBumpCount}{' '}
                  {priorityBumpCount === 1 ? 'existing rule' : 'existing rules'} — review the rule
                  list to confirm this is what you want.
                </InfoBanner>
              )}
            </fieldset>
          </div>
        )}

        {/* Hidden status that helpfully announces in-scope event count to AT
            users. The visible "Destination:" line above already orients
            sighted users; this hidden line covers a screen-reader gap. */}
        <span className={styles.srOnly}>
          {eventsInScope.length} {eventsInScope.length === 1 ? 'event' : 'events'} are in scope.
        </span>
      </div>

      <div className={styles.footer}>
        {conflictTriggered ? (
          <Button variant="primary" onClick={onClose} data-testid="wlr-classify-done">
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onBack} disabled={submitting}>
              ← Back
            </Button>
            <Button variant="secondary" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={submitting}
              disabled={submitting || formInvalid}
              data-testid="wlr-classify-apply"
            >
              {submitting ? 'Applying…' : 'Apply'}
            </Button>
          </>
        )}
      </div>
    </form>
  );
}

// ── Conflict-warning copy (locked decision #12) ─────────────────────────────

function formatConflictMessage(report: {
  eventsCaughtByExistingRule: number;
  sampleRuleNames: string[];
}): string {
  const n = report.eventsCaughtByExistingRule;
  const names = report.sampleRuleNames;
  if (names.length === 0) {
    return `${n} of these events would be matched by an existing rule first — your new rule will only catch the rest.`;
  }
  const head = `${n} of these events would be matched by rule "${names[0]}" first — your new rule will only catch the rest.`;
  if (names.length === 1) return head;
  const extra = names.length - 1;
  return `${head} (and ${extra} other ${extra === 1 ? 'rule' : 'rules'})`;
}
